<template>
    <div class="bg-white rounded pt-7 pb-6 mt-10 shadow-card">
        <title-section class="px-7" :title="title" @go-back="goBack" />
        <div class="mt-6 ml-1">
            <h5 class="text-xs px-7">{{ subTitle }}</h5>
            <div v-if="!showSelectAccount" class="px-7">
                <input-with-button
                    v-model="cpfCnpjName"
                    :placeholder="'Digite um CPF/CNPJ ou nome de um favorecido já cadastrado'"
                    :text-button="'Adicionar novo'"
                    :href-button="'/contact/create'"
                    :value="cpfCnpjName"
                    @input="$emit('refresh-favored', $event)"
                />
                <registered-list
                    class="mt-7"
                    :loading="favoreds.loading"
                    :title="'Favorecidos registrados'"
                    :data="registaredFavoreds"
                    :messageEmpty="messageEmptyFavored"
                    @remove-item="(favoredSelected = $event, removeConfirm = true)"
                    @handle-click-item="(favoredSelected = $event, showSelectAccount = true)"
                />
            </div>
            <div v-else >
                <div class="px-7">
                    <favored class="pointer-events-none border-none pl-0 pt-2" :favored="favoredSelected" />
                    <h5 class="text-xs mt-3">Conta</h5>
                    <input-with-button
                        v-model="nickname"
                        :placeholder="'Digite o apelido da conta'"
                        :text-button="'Adicionar nova'"
                        :href-button="'/contact/create'"
                    />
                </div>
                <div class="px-6">
                    <h5 class="text-xs mt-7 mb-3">Contas disponíveis</h5>
                    <accounts-table
                        :accounts="accountsFavored"
                        @remove-item="(accountSelected = $event, removeConfirm = true)"
                        @handle-click-item="selectAccount"
                    />
                </div>
            </div>
        </div>

        <jet-confirmation-modal
            :scroll="false"
            :show="removeConfirm"
            @close="removeConfirm = false">
            <template #title>
                {{ showSelectAccount ? 'Remover conta' : 'Remover cadastro' }}
            </template>
            <template #content>
                <p v-html="messageToDelete" />
            </template>
            <template #footer>
                <jet-secondary-button @click.native="removeConfirm = false">
                    Não
                </jet-secondary-button>
                <jet-button
                    :class="formRemoveFavored.processing ? 'md:bg-gray-300 md:hover:bg-gray-300' : ''"
                    @click.native="remove"
                >
                    <i v-show="formRemoveFavored.processing" class="fas fa-spinner animate-spin mr-2" /> Remover
                </jet-button>
            </template>
        </jet-confirmation-modal>
    </div>
</template>

<script>
import JetInput from "../../../Jetstream/Input.vue";
import JetInputError from "../../../Jetstream/InputError.vue";
import JetConfirmationModal from "../../../Jetstream/ConfirmationModal.vue";
import JetButton from "../../../Jetstream/Button.vue";
import JetSecondaryButton from "../../../Jetstream/SecondaryButton.vue";
import TitleSection from '../components/TitleSection.vue';
import RegisteredList from '../components/RegisteredList.vue';
import Favored from "../components/Favored.vue";
import AccountsTable from "../components/AccountsTable.vue";
import InputWithButton from "../components/InputWithButton.vue";
import { generateRandomColor, getNameInitials } from "../utils/utils";

export default {
    name: 'SelectFavored',
    props: {
        favoreds: { type: Object, default: () => ({}) },
        accounts: { type: Object, default: () => ({}) },
    },
    data(){
        return {
            errorCpfCnpj: '',
            errorNickname: '',
            cpfCnpjName: '',
            nickname: '',
            showSelectAccount: false,
            favoredSelected: {},
            accountSelected: {},
            removeConfirm: false,
            formRemoveFavored: this.$inertia.form({
                _method: 'DELETE',
                redirectRoute: '/send/between-accounts'
            }),
            formRemoveAccount: this.$inertia.form({
                _method: 'PUT' ,
                name: null,
                federal_document: null,
                transfer_accounts: [],
                limits: [],
                redirectRoute: '/send/between-accounts'
            }),
        }
    },
    computed: {
        messageEmptyFavored(){
            return !this.favoreds.content?.length && this.cpfCnpjName.length
                ? `Nenhum resultado para ${this.cpfCnpjName}`
                : 'Nenhum favorecido.'
        },
        messageToDelete(){
            const spanBold = (text) => `<span class="font-semibold">${text}</span>`
            return this.showSelectAccount
                ? `Você confirma a remoção da conta ${spanBold(this.accountSelected.name ?? 'Sem apelido')} do banco ${spanBold(this.accountSelected.bank?.name)} agência ${spanBold(this.accountSelected.agency)} conta ${spanBold(this.accountSelected.number)}?`
                : `Você confirma a remoção do favorecido ${spanBold(this.favoredSelected.name)} com o documento ${spanBold(this.favoredSelected.federal_document)}?`
        },
        title(){
            return !this.showSelectAccount ? 'Selecione um favorecido' : 'Selecione a conta de destino'
        },
        subTitle(){
            return !this.showSelectAccount ? 'Favorecido' : 'Favorecido selecionado'
        },
        accountsFavored(){
            return this.favoredSelected.transfer_accounts
                .filter((account) => ['corrente', 'poupança'].includes(account.type.toLowerCase()))
                .filter(account => {
                    if(!['274', '457', '529'].includes(account.bank.id)) return false
                    if(!this.nickname) return true
                    if(account.name?.toLowerCase().match(this.nickname.toLowerCase())) return true
                    return false
                })
        },
        registaredFavoreds(){
            return this.favoreds.content?.map((favored) => {
                const document = favored.federal_document.replace(/\D/g, '')
                const isCpf = document.length === 11
                return {
                    ...favored,
                    bgItemList: generateRandomColor(),
                    initialsName: getNameInitials(favored.name),
                    document: `${isCpf ? 'CPF' : 'CNPJ'}: ${favored.federal_document}`
                }
            })
        },
    },
    methods: {
        selectAccount(account){
            this.favoredSelected.accountSelected = account
            this.$emit('next', this.favoredSelected)
        },
        goBack(){
            if(this.showSelectAccount){
                this.showSelectAccount = false
                return
            }
            window.history.back()
        },
        remove() {
            const endpoint = `/contact/${this.favoredSelected.id}`;
            if(!this.showSelectAccount){
                this.formRemoveFavored.post(endpoint, { preserveState: false })
                return
            }

            const accountRemoved = this.favoredSelected.transfer_accounts
                .filter((account) => account.number !== this.accountSelected.number || account.bank.id !== this.accountSelected.bank.id)
            this.formRemoveAccount.transfer_accounts = accountRemoved
            this.formRemoveAccount.name = this.favoredSelected.name
            this.formRemoveAccount.federal_document = this.favoredSelected.federal_document

            this.formRemoveAccount.post(`/contact/${this.favoredSelected.id}`, {
                onSuccess: () => {
                    this.favoredSelected.transfer_accounts = accountRemoved
                    this.removeConfirm = false
                },
            })
        },
    },
    components: {
        JetInput,
        JetInputError,
        JetConfirmationModal,
        JetButton,
        JetSecondaryButton,
        TitleSection,
        RegisteredList,
        Favored,
        AccountsTable,
        InputWithButton
    },
};
</script>
